import { formatDate } from '../../services/primitives/date-utils';
import { ROSTER_DOS_PRESETS, rosterIsClaimLocked, rosterIsoDate } from '../../services/roster/roster.claims';
import type { RosterClaim } from '../../services/roster/roster.types';
import { ToastContext } from '../../services/toast/toast';
import { RosterClaimsFlow } from '../../stores/roster/roster-claims-flow';
import { RosterStore } from '../../stores/roster/roster.store';
import { Banner } from '../base/banner/banner';
import { BaseButton } from '../base/button/button';
import { ComingSoon } from '../base/coming-soon/coming-soon';
import { BaseCheckbox } from '../base/checkbox/checkbox';
import { DatePicker } from '../base/datepicker/datepicker';
import { BaseDialog } from '../base/dialog/dialog';
import { Toggle } from '../base/toggle/toggle';
import { DataTable } from '../data-table/data-table';
import { DataTablePresenter, ITableColumn } from '../data-table/data-table.presenter';
import { mdiInformationOutline, mdiLockOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { autorun } from 'mobx';
import { observer } from 'mobx-react';
import React, { useContext, useEffect, useMemo } from 'react';
import { CellProps } from 'react-table';
import * as S from './roster-claims-dialog.styles';
import * as T from './roster-table.styles';

/**
 * "Apply changes to existing claims?" (FE-109), shown after an existing provider or facility is
 * saved. The save has already happened; this only decides what, if anything, happens to the claims
 * that reference the record. Every way out that does not apply says so: "existing claims unchanged".
 */
export const RosterClaimsDialog = observer(({ store }: { store: RosterStore }): React.ReactElement | null => {
    const flow = store.claimsFlow;
    const toast = useContext(ToastContext);

    const finish = (message: string): void => {
        store.closeClaimsFlow();
        toast.notify?.(message, 'right', 'light');
    };
    const leaveClaims = (): void => finish('Changes saved. Existing claims unchanged.');

    // Nothing references the record: there is nothing to ask.
    useEffect(() => {
        if (flow?.status === 'ready' && flow.claims.length === 0) {
            finish('Changes saved.');
        }
    }, [flow, flow?.status, flow?.claims.length]);

    if (!flow || (flow.status === 'ready' && flow.claims.length === 0)) {
        return null;
    }

    const selecting = flow.step === 'select';

    return (
        <BaseDialog
            open={true}
            onClose={leaveClaims}
            renderTitle={selecting ? 'Select claims to update' : 'Apply changes to existing claims?'}
            maxWidth={selecting ? 'lg' : 'sm'}
            fullWidth={true}
            disableBackdropClick={true}
        >
            {selecting ? (
                <SelectStep flow={flow} onCancel={leaveClaims} onDone={finish} />
            ) : (
                <AskStep flow={flow} onSkip={leaveClaims} />
            )}
        </BaseDialog>
    );
});

const AskStep = observer(({ flow, onSkip }: { flow: RosterClaimsFlow; onSkip: () => void }): React.ReactElement => {
    if (flow.status === 'loading') {
        return <S.Body>Looking up the claims that reference {flow.name}…</S.Body>;
    }
    if (flow.status === 'error') {
        return (
            <S.Body>
                <Banner tone="danger" title="Couldn't look up the claims that reference this record">
                    {flow.error}. Your changes were saved; existing claims were not changed. This is unknown, not
                    "none".
                </Banner>
                <S.Buttons>
                    <BaseButton type="button" variant="outlined" onClick={onSkip}>
                        Close
                    </BaseButton>
                    <BaseButton type="button" variant="primary" onClick={(): void => void flow.load()}>
                        Try again
                    </BaseButton>
                </S.Buttons>
            </S.Body>
        );
    }
    const count = flow.claims.length;
    return (
        <S.Body>
            <S.Text>
                {count} existing {count === 1 ? 'claim references' : 'claims reference'} {flow.name}. Updating them
                re-applies the new {flow.noun} details to each claim.
            </S.Text>
            <S.Note>
                <Icon path={mdiInformationOutline} size={0.7} />
                <span>
                    Claims already paid cannot be amended. Updated claims can be retransmitted to the payer as corrected
                    claims.
                </span>
            </S.Note>
            <S.Buttons>
                <BaseButton type="button" variant="outlined" onClick={onSkip}>
                    No — new claims only
                </BaseButton>
                <BaseButton type="button" variant="primary" onClick={flow.chooseClaims}>
                    Select claims
                </BaseButton>
            </S.Buttons>
        </S.Body>
    );
});

interface ISelectStepProps {
    flow: RosterClaimsFlow;
    onCancel: () => void;
    onDone: (message: string) => void;
}

const SelectStep = observer(({ flow, onCancel, onDone }: ISelectStepProps): React.ReactElement => {
    const toast = useContext(ToastContext);
    const presenter = useMemo(
        () =>
            new DataTablePresenter<RosterClaim>({
                name: 'roster_claims',
                data: [],
                columns: claimColumns(flow),
                filterSchemas: [],
                filters: [],
            }),
        [flow],
    );

    useEffect(
        () =>
            autorun(() => {
                presenter.onDataUpdate(flow.inRange);
                presenter.onDataTotalChange(flow.inRange.length);
            }),
        [flow, presenter],
    );

    const count = flow.selection.length;

    const apply = async (): Promise<void> => {
        try {
            const result = await flow.apply();
            onDone(
                `${result.updated} ${result.updated === 1 ? 'claim' : 'claims'} updated${
                    result.queuedForTransmission ? ' · queued for transmission.' : '.'
                }`,
            );
        } catch (error) {
            toast.notify?.(
                `Updating claims failed: ${error instanceof Error ? error.message : 'request failed'}.`,
                'right',
                'warning',
            );
        }
    };

    const updateButton = (
        <BaseButton
            type="button"
            variant="primary"
            onClick={apply}
            disabled={count === 0 || flow.applying || !flow.applyAvailable}
        >
            {flow.applying
                ? 'Updating…'
                : count
                  ? `Update ${count} ${count === 1 ? 'claim' : 'claims'}`
                  : 'Update claims'}
        </BaseButton>
    );

    return (
        <S.Body>
            <S.Text>Filter by date of service, then choose which claims to re-apply {flow.name} to.</S.Text>
            <S.Filters>
                <S.DateField>
                    <S.Label>DOS from</S.Label>
                    <DatePicker
                        value={flow.range.from ?? ''}
                        onDateSelected={(date): void =>
                            flow.setRange({ ...flow.range, from: date ? rosterIsoDate(date) : undefined })
                        }
                    />
                </S.DateField>
                <S.DateField>
                    <S.Label>DOS to</S.Label>
                    <DatePicker
                        value={flow.range.to ?? ''}
                        onDateSelected={(date): void =>
                            flow.setRange({ ...flow.range, to: date ? rosterIsoDate(date) : undefined })
                        }
                    />
                </S.DateField>
                <S.Presets>
                    {ROSTER_DOS_PRESETS.map(preset => (
                        <BaseButton
                            key={preset.key}
                            type="button"
                            variant="outlined"
                            size="small"
                            onClick={(): void => flow.applyPreset(preset.key)}
                        >
                            {preset.label}
                        </BaseButton>
                    ))}
                </S.Presets>
            </S.Filters>
            <S.CountRow>
                <span aria-live="polite">
                    {flow.inRange.length} {flow.inRange.length === 1 ? 'claim' : 'claims'} · {count} selected
                    {flow.lockedInRange > 0 && ` · ${flow.lockedInRange} paid, locked`}
                </span>
                <BaseButton type="button" variant="text" size="small" onClick={flow.toggleAll}>
                    {flow.allSelected ? 'Deselect all' : 'Select all in range'}
                </BaseButton>
            </S.CountRow>
            <S.TableFrame>
                <DataTable
                    presenter={presenter}
                    hideFiltering={true}
                    hideNavigation={true}
                    hidePagination={true}
                    hideSettings={true}
                    disableSorting={true}
                    NotFoundComponent={(): React.ReactElement => (
                        <T.Empty>
                            <T.EmptyTitle>No claims in this date range.</T.EmptyTitle>
                        </T.Empty>
                    )}
                />
            </S.TableFrame>
            <S.Footer>
                <S.ToggleRow>
                    <Toggle value={flow.retransmit} onChange={flow.setRetransmit} />
                    <span>Transmit updated claims to payer</span>
                </S.ToggleRow>
                <S.Buttons>
                    <BaseButton type="button" variant="outlined" onClick={onCancel}>
                        Cancel
                    </BaseButton>
                    {flow.applyAvailable ? (
                        updateButton
                    ) : (
                        <ComingSoon hint="updating existing claims from the roster">{updateButton}</ComingSoon>
                    )}
                </S.Buttons>
            </S.Footer>
        </S.Body>
    );
});

function claimColumns(flow: RosterClaimsFlow): ITableColumn<RosterClaim>[] {
    return [
        {
            id: 'pick',
            Header: '',
            width: 48,
            accessor: 'id',
            Cell: observer(
                ({ row }: CellProps<RosterClaim>): React.ReactElement =>
                    rosterIsClaimLocked(row.original) ? (
                        <S.Locked title="Settled by the payer — cannot amend">
                            <Icon path={mdiLockOutline} size={0.7} />
                        </S.Locked>
                    ) : (
                        <BaseCheckbox
                            variant="small"
                            checked={flow.isSelected(row.original)}
                            onChange={(): void => flow.toggle(row.original)}
                            aria-label={`Select claim ${row.original.id}`}
                        />
                    ),
            ),
        },
        { id: 'id', Header: 'Claim ID', accessor: 'id', width: 180 },
        {
            id: 'dos',
            Header: 'DOS',
            accessor: 'dos',
            width: 110,
            Cell: ({ value }: CellProps<RosterClaim, string>): string => formatDate(value, 'MMM d yyyy'),
        },
        { id: 'patient', Header: 'Patient', accessor: 'patientName', width: 160 },
        { id: 'payer', Header: 'Payer', accessor: 'payerName', width: 180 },
        {
            id: 'charge',
            Header: 'Charge',
            accessor: 'chargeAmount',
            width: 90,
            Cell: ({ value }: CellProps<RosterClaim, string>): string => `$${value}`,
        },
        {
            id: 'status',
            Header: 'Status',
            accessor: 'status',
            width: 130,
            Cell: ({ row, value }: CellProps<RosterClaim, string>): React.ReactElement => (
                <T.Chip tone={rosterIsClaimLocked(row.original) ? 'warning' : 'primary'}>{value}</T.Chip>
            ),
        },
    ];
}
